import { Component, OnInit } from '@angular/core';
import { first } from 'rxjs';
import { ModalService, MODAL } from '../../services/modal.service';
import { CustomerService } from '../../services/customer.service';
import { CurrentService } from 'src/app/services/current.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CONFIG_CONTEXT } from 'src/app/enum/config-context.enum';
import { DatasService } from 'src/app/services/datas.service';
import { getAppGtmRef, getAppUrl } from '../../services/helpers/app-context-helper';
import { GTM } from 'src/app/services/gtm.service';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { StatisticService } from 'src/app/services/statistic.service';
import { StatisticTypeEnum } from 'src/app/enum/statistic-type.enum';
import { CastometreService } from 'src/app/services/castometre/castometre.service';

/**
 * Messages associés au paramètre `ciamError` que le callback AuthApp et le guard de session
 * posent dans l'URL avant de ramener ici. Une valeur inconnue n'affiche rien.
 */
const CIAM_ERROR_MESSAGES: Record<string, string> = {
  callback: 'popins.ciam-error.callback',
  unavailable: 'popins.ciam-error.unavailable',
};

/**
 * Posés dans l'URL de retour d'AuthApp quand le client a cliqué sur « Créer un nouveau projet »
 * ou « Tous mes projets » sans être connecté : l'accueil reprend alors l'action interrompue,
 * sans second clic.
 */
const NEW_PROJECT_QUERY_PARAM = 'nouveau-projet';
const PROJECTS_QUERY_PARAM = 'projets';

@Component({
  selector: 'ng-casto-home',
  templateUrl: './home.component.html',
})
export class HomeComponent implements OnInit {
  private readonly castometreDistribId = 'castometre';
  protected readonly faArrowRight = faArrowRight;

  constructor(
    public datas: DatasService,
    public gtm: GTM,
    protected castometre: CastometreService,
    private readonly modalService: ModalService,
    private readonly customer: CustomerService,
    private readonly current: CurrentService,
    private readonly router: Router,
    private readonly statistic: StatisticService,
    private readonly route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    this.gtm.trackPage(this.router.url, 'Accueil');
    this.displayCiamError();
    this.resumeAfterLogin();
  }

  /**
   * Le retour d'AuthApp et le guard de session ramènent à l'accueil avec un code d'erreur dans
   * l'URL. Sans ce relais, le client se retrouverait ici sans la moindre explication.
   */
  private displayCiamError(): void {
    const contentRef = CIAM_ERROR_MESSAGES[this.route.snapshot.queryParamMap.get('ciamError') ?? ''];
    if (!contentRef) {
      return;
    }

    // Le code ne vaut que pour cet affichage : on le retire de l'URL pour qu'un rafraîchissement
    // ou un lien partagé ne rejoue pas le message.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ciamError: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    this.modalService.display(MODAL.MESSAGE, {
      titleRef: 'popins.ciam-error.title',
      contentRef,
      buttons: [
        {
          labelRef: 'generic.close',
          primary: true,
          action: () => this.modalService.close(),
        },
      ],
    });
  }

  private resumeAfterLogin(): void {
    const params = this.route.snapshot.queryParamMap;
    const newProjectContext = params.get(NEW_PROJECT_QUERY_PARAM);
    const projectsTarget = params.get(PROJECTS_QUERY_PARAM);
    if (!newProjectContext && !projectsTarget) {
      return;
    }

    // L'action ne vaut que pour ce retour : un rafraîchissement ou un lien partagé ne la rejoue pas.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [NEW_PROJECT_QUERY_PARAM]: null, [PROJECTS_QUERY_PARAM]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });

    if (!this.customer.isLogged) {
      return;
    }

    if (newProjectContext) {
      // La popup de complétion éventuelle reste affichée par-dessus le configurateur.
      void this.openConfigurator(newProjectContext);
      return;
    }

    // Une fiche incomplète affiche sa popup de complétion, bloquante : la liste attend sa fermeture.
    if (this.modalService.currentId === MODAL.NONE) {
      this.openProjects(projectsTarget);
    } else {
      this.modalService.closeEvents.pipe(first()).subscribe(() => {
        if (this.customer.isLogged) {
          this.openProjects(projectsTarget);
        }
      });
    }
  }

  trackCastometreAppStore(): void {
    this.gtm.trackEvent('telecharger-app-castometre-accueil');
    this.sendHomeStatistic(StatisticTypeEnum.CASTOMETRE_DOWNLOAD_CLICKED_COUNT);
  }

  /**
   * Un projet ne se démarre que connecté : la popup de connexion s'interpose, et le retour
   * d'AuthApp passe par l'accueil, qui ouvre le configurateur choisi comme le ferait le clic.
   */
  async startNewProject(context: string): Promise<void> {
    this.gtm.trackEvent('creer-projet-accueil', { projectType: getAppGtmRef(context) });

    if (!this.customer.isLogged) {
      this.modalService.display(MODAL.LOGIN, {
        returnUrl: `/?${NEW_PROJECT_QUERY_PARAM}=${encodeURIComponent(context)}`,
        loginCallBack: () => {
          this.modalService.close();
          void this.openConfigurator(context);
        },
      });
      return;
    }

    await this.openConfigurator(context);
  }

  private async openConfigurator(context: string): Promise<void> {
    // Le contexte peut venir de l'URL de retour : un configurateur inconnu ne mène nulle part.
    const url = this.getConfiguratorUrl(context);
    if (!url) {
      return;
    }

    this.current.reset();
    this.customer.context = context;
    await this.router.navigateByUrl(url);
  }

  private getConfiguratorUrl(context: string): string | null {
    switch (context) {
      case CONFIG_CONTEXT.KITCHEN:
        return '/cuisine';
      case CONFIG_CONTEXT.STORAGE:
        return '/rangements';
      case CONFIG_CONTEXT.BATHROOM:
        return '/salle-de-bain';
      default: {
        const appUrl = getAppUrl(context);
        return appUrl ? `${appUrl}/configuration` : null;
      }
    }
  }

  goProjects(distribTarget: string) {
    if (this.customer.isLogged) {
      this.openProjects(distribTarget);
    } else {
      this.modalService.display(MODAL.LOGIN, {
        returnUrl: `/?${PROJECTS_QUERY_PARAM}=${encodeURIComponent(distribTarget)}`,
        loginCallBack: () => this.openProjects(distribTarget),
      });
    }
  }

  private openProjects(distribTarget: string): void {
    this.gtm.trackEvent('selectionner-projet-accueil', { projectType: getAppGtmRef(distribTarget) });
    this.modalService.display(MODAL.PROJECTS, {
      customerId: this.customer.userDatas.id,
      tabKey: distribTarget,
    });
  }

  goCastometreProjects() {
    this.sendHomeStatistic(StatisticTypeEnum.HOME_PROJECTS_OPENED_COUNT);
    this.goProjects(CONFIG_CONTEXT.KITCHEN);
  }

  private sendHomeStatistic(statisticType: StatisticTypeEnum): void {
    this.statistic.sendStatistic(statisticType, 1, this.castometreDistribId);
  }
}
